import { useState } from "react";
import { ModuleAccessGuard } from "@/components/ModuleAccessGuard";
import { DataTable } from "@/components/shared/DataTable";
import { FilterBar } from "@/components/shared/FilterBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useSitesAPI, useCreateSite, useUpdateSite, useDeleteSite } from "@/hooks/useOnboarding";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Plus, Edit, Trash2, MoreHorizontal, Navigation, Download } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { Column } from "@/components/shared/DataTable";

export default function Sites() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<any>(null);
  const [subGrids, setSubGrids] = useState<Array<{name: string, id: string}>>([]);
  const { toast } = useToast();

  const offset = (page - 1) * 20;
  const { data: sitesData, isLoading } = useSitesAPI({ offset, limit: 20, search, ...filters });
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();

  const [formData, setFormData] = useState({
    customer_id: "",
    site_name: "",
    site_reference_id: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    latitude: "",
    longitude: "",
    valid_from: "",
    valid_to: "",
    status: "active"
  });

  const handleUseLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          });
          toast({ title: "Location captured successfully" });
        },
        () => toast({ title: "Unable to get location", variant: "destructive" })
      );
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...formData, sub_grids: subGrids };
      if (editingSite) {
        await updateSite.mutateAsync({ siteId: editingSite.id, updates: payload });
        toast({ title: "Site updated successfully" });
      } else {
        await createSite.mutateAsync(payload);
        toast({ title: "Site created successfully" });
      }
      setIsCreateOpen(false);
      setEditingSite(null);
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "", site_name: "", site_reference_id: "", address: "", city: "",
      state: "", country: "", postal_code: "", latitude: "", longitude: "",
      valid_from: "", valid_to: "", status: "active"
    });
    setSubGrids([]);
  };

  const addSubGrid = () => setSubGrids([...subGrids, { name: "", id: "" }]);
  const removeSubGrid = (index: number) => setSubGrids(subGrids.filter((_, i) => i !== index));
  const updateSubGrid = (index: number, field: 'name' | 'id', value: string) => {
    const updated = [...subGrids];
    updated[index][field] = value;
    setSubGrids(updated);
  };

  const columns: Column<any>[] = [
    { key: "customer_name", header: "Customer", render: (item) => item.customer_name },
    { key: "site_name", header: "Site Name", render: (item) => item.site_name },
    { key: "address", header: "Address", render: (item) => item.address },
    { key: "city", header: "City", render: (item) => item.city },
    { key: "state", header: "State", render: (item) => item.state },
    { key: "country", header: "Country", render: (item) => item.country },
    { key: "postal_code", header: "Postal Code", render: (item) => item.postal_code },
    { key: "valid_from", header: "Move In", render: (item) => item.valid_from },
    { key: "valid_to", header: "Move Out", render: (item) => item.valid_to },
    { key: "site_reference_id", header: "Site Ref ID", render: (item) => item.site_reference_id },
    {
      key: "sub_grids",
      header: "Sub Grids",
      render: (item) => {
        const grids = item.sub_grids || [];
        return <Badge variant="secondary">{grids.length}</Badge>;
      }
    },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        const status = item.status;
        const variant = status === "active" ? "default" : "secondary";
        return <Badge variant={variant}>{status}</Badge>;
      }
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => { setEditingSite(item); setIsCreateOpen(true); }}>
              <Edit className="mr-2 h-4 w-4" />Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => deleteSite.mutate(item.id)}>
              <Trash2 className="mr-2 h-4 w-4" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const filterConfig = [
    { key: "customer_name", label: "Customer", type: "select" as const, options: [] },
    { key: "site_name", label: "Site Name", type: "select" as const, options: [] }
  ];

  return (
    <ModuleAccessGuard moduleId="onboarding">
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Sites</h1>
            <p className="text-muted-foreground mt-2">Manage customer sites and their associated meters.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="hero" onClick={resetForm}><Plus className="h-4 w-4 mr-2" />Create Site</Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingSite ? "Edit Site" : "Create New Site"}</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="basic">Basic</TabsTrigger>
                    <TabsTrigger value="location">Location</TabsTrigger>
                    <TabsTrigger value="validity">Validity</TabsTrigger>
                    <TabsTrigger value="subgrids">Sub Grids</TabsTrigger>
                    <TabsTrigger value="status">Status</TabsTrigger>
                  </TabsList>
                  <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Customer *</Label>
                        <Input placeholder="Customer ID" value={formData.customer_id} onChange={(e) => setFormData({...formData, customer_id: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Site Name *</Label>
                        <Input value={formData.site_name} onChange={(e) => setFormData({...formData, site_name: e.target.value})} />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Site Reference ID</Label>
                        <Input value={formData.site_reference_id} onChange={(e) => setFormData({...formData, site_reference_id: e.target.value})} />
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="location" className="space-y-4">
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Input value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Postal Code</Label>
                        <Input value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Latitude</Label>
                        <Input value={formData.latitude} onChange={(e) => setFormData({...formData, latitude: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Longitude</Label>
                        <Input value={formData.longitude} onChange={(e) => setFormData({...formData, longitude: e.target.value})} />
                      </div>
                    </div>
                    <Button type="button" variant="outline" onClick={handleUseLocation}>
                      <Navigation className="h-4 w-4 mr-2" />Use My Location
                    </Button>
                  </TabsContent>
                  <TabsContent value="validity" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Move In Date</Label>
                        <Input type="date" value={formData.valid_from} onChange={(e) => setFormData({...formData, valid_from: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>Move Out Date</Label>
                        <Input type="date" value={formData.valid_to} onChange={(e) => setFormData({...formData, valid_to: e.target.value})} />
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="subgrids" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label>Sub Grids</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addSubGrid}>
                        <Plus className="h-4 w-4 mr-2" />Add Sub Grid
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {subGrids.map((grid, index) => (
                        <div key={index} className="flex gap-2">
                          <Input placeholder="Name" value={grid.name} onChange={(e) => updateSubGrid(index, 'name', e.target.value)} />
                          <Input placeholder="ID" value={grid.id} onChange={(e) => updateSubGrid(index, 'id', e.target.value)} />
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeSubGrid(index)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="status" className="space-y-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                </Tabs>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                  <Button onClick={handleSubmit}>{editingSite ? "Update" : "Create"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Sites</p>
                  <p className="text-2xl font-bold">{sitesData?.total || 0}</p>
                </div>
                <MapPin className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Sites</p>
                  <p className="text-2xl font-bold">{sitesData?.active || 0}</p>
                </div>
                <MapPin className="h-5 w-5 text-success" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sites with Meters</p>
                  <p className="text-2xl font-bold">{sitesData?.with_meters || 0}</p>
                </div>
                <MapPin className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sites with Accounts</p>
                  <p className="text-2xl font-bold">{sitesData?.with_accounts || 0}</p>
                </div>
                <MapPin className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        <FilterBar
          searchPlaceholder="Search sites..."
          searchValue={search}
          onSearchChange={setSearch}
          filters={filterConfig}
          activeFilters={filters}
          onFilterChange={(key, value) => setFilters({...filters, [key]: value})}
          onClearFilters={() => setFilters({})}
        />

        <DataTable
          data={sitesData?.data || []}
          columns={columns}
          totalCount={sitesData?.total || 0}
          pageSize={20}
          currentPage={page}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No sites found"
          getRowId={(item) => item.id}
        />
      </div>
    </ModuleAccessGuard>
  );
}
